import { memo, useCallback, useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import type { Drill, DrillShotType } from '../types/lesson';
import { getAllDrillsForCoach } from '../services/drills';
import { AddDrillModal } from './AddDrillModal';
import VideoFrameThumb from './VideoFrameThumb';
import { EditorVideo } from './videoEditor/EditorVideo';
import { useLazyFrame } from './videoEditor/rowThumbnail';
import { usePhoneLayout } from './videoEditor/usePhoneLayout';
import { LogoSvg } from './Logo';
import { IosSheet } from './videoEditor/IosSheet';
import { debugNote } from './videoEditor/debugNotes';

function isPlaceholderUrl(url: string | undefined): boolean {
  return !url || url.startsWith('https://placeholder.drill/');
}

interface DrillBrowserProps {
  coachId: string;
  /** IDs of drills already attached to this lesson */
  selectedDrillIds: string[];
  /** Called when coach toggles a drill on/off */
  onToggleDrill: (drill: Drill) => void;
  /**
   * True when rendered inside the coach lesson editor. Opts the editor's
   * phone behaviour in without changing the admin Clip Library, which shares
   * this component.
   */
  inLessonEditor?: boolean;
}

const SHOT_TYPE_LABELS: Record<DrillShotType, string> = {
  forehand: 'Forehand',
  backhand: 'Backhand',
  serve: 'Serve',
  volley: 'Volley',
  return: 'Return',
  movement: 'Movement',
  general: 'General',
};

const SHOT_TYPE_ORDER: DrillShotType[] = [
  'forehand', 'backhand', 'serve', 'volley', 'return', 'movement', 'general',
];

/**
 * The lesson editor's drill list, per coach, for the session.
 *
 * Every visit to the Drills tab used to re-read all 476 stock drills and the
 * coach's own, because the tab unmounts this component on the way out. The
 * list barely changes inside a session: adding a custom drill reloads and
 * refreshes it below, and a page reload starts clean.
 */
const editorDrillsCache = new Map<string, Drill[]>();

/**
 * A card's picture in the lesson editor: the stored thumbnail, or a frame made
 * once the card scrolls into view, three at a time across the editor, and
 * cached for the session under the same key the comparison picker uses.
 *
 * Replaces VideoFrameThumb here, which opened a hidden video for every drill
 * at once: 28 on staging, each on a 5s timeout that, sharing the connection
 * 28 ways, none of them met, and none of them aborted, so the downloads ran
 * on behind a placeholder. The web-optimized file is used when there is one;
 * it is the smaller download.
 */
/**
 * Phone (spec 13.3): the drill's description, two lines until tapped, then
 * in full in place. A second tap folds it again.
 */
function ExpandableDescription({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  return (
    <button
      type="button"
      onClick={() => setOpen((o) => !o)}
      aria-expanded={open}
      // Opens in full on tap: two lines until tapped, then the whole description in place.
      className={`block w-full text-left text-xs text-[#8899AA] leading-relaxed mb-3 ios-press ${open ? 'whitespace-pre-line' : 'line-clamp-2'}`}
    >
      {text}
    </button>
  );
}

function EditorDrillFrame({ drill }: { drill: Drill }) {
  const url = drill.webOptimizedVideoUrl || drill.videoUrl;
  const hasVideo = !isPlaceholderUrl(url);
  const { slotRef, frame } = useLazyFrame({
    cacheKey: `drill:${drill.id}`,
    videoUrl: hasVideo ? url : undefined,
    skip: !!drill.thumbnailUrl || !hasVideo,
  });
  const src = drill.thumbnailUrl || frame;
  return (
    <div ref={slotRef} className="w-full h-full flex items-center justify-center">
      {src ? (
        <img src={src} alt={drill.title} className="w-full h-full object-cover" />
      ) : (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="#5C6B75">
          <polygon points="6 4 20 12 6 20 6 4" />
        </svg>
      )}
    </div>
  );
}

/* ── Phone (spec 15.21) ────────────────────────────────────────────────────
   The list makes no <video> and grabs no frames: a card is its stored
   thumbnail, the poster a Cloud Function wrote next to the drill's video
   (functions/src/drillThumbnails.ts), as a plain lazy <img> with long cache
   headers, or a flat placeholder with the tennis ball mark. No spinner. The
   list itself is kept for the session (editorDrillsCache), so a return to the
   Drills tab reads nothing. Cards render 20 at a time. */
const PHONE_DRILL_PAGE = 20;

function PhoneDrillThumb({ drill }: { drill: Drill }) {
  const url = drill.thumbnailUrl;
  return url ? (
    <img src={url} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
  ) : (
    <div data-testid="drill-placeholder" className="w-full h-full flex items-center justify-center" style={{ background: 'var(--editor-surface)', color: 'var(--editor-text-muted)' }} aria-hidden="true">
      <LogoSvg h={22} variant="dark" />
    </div>
  );
}

/* ── Phone (spec 15.25): one card ──────────────────────────────────────────
   Two columns, 12 gutter (the organizer gives 16 round the grid). A card is
   its 16:9 thumbnail (radius 10), the title (Subhead 15/20 semibold, two
   lines reserved so every row is the same height), then a row with the
   category chip at the left and the info and add buttons (36, ghost) at the
   right. No padding, ring or hover lift round the card: the old card's
   selected ring (ring-2) and .btn-press hover shadow, which iOS keeps on
   after a tap, drew what read as a border round the first row. Memoised
   (15.26): a card re-renders only when its drill or its selected state
   changes. */
const PHONE_CHIP = 'inline-flex items-center min-h-6 px-2 rounded-full text-[12px] leading-[16px] font-medium';
const PHONE_GHOST = 'relative w-9 h-9 flex-none rounded-full inline-flex items-center justify-center ios-press focus:outline-none focus-visible:ring-2 focus-visible:ring-db-accent/40';

const PhoneDrillCard = memo(function PhoneDrillCard({ drill, selected, onOpen, onToggle }: {
  drill: Drill;
  selected: boolean;
  onOpen: (drill: Drill) => void;
  onToggle: (drill: Drill) => void;
}) {
  return (
    <div data-testid="drill-browser-item" className="flex flex-col gap-2 min-w-0">
      <button
        type="button"
        onClick={() => onOpen(drill)}
        className="aspect-video rounded-[10px] overflow-hidden relative ios-press focus:outline-none focus-visible:ring-2 focus-visible:ring-db-accent/40"
        style={{ background: 'var(--editor-surface)' }}
        aria-label={`Open ${drill.title}`}
      >
        <PhoneDrillThumb drill={drill} />
      </button>
      <div className="text-[15px] leading-[20px] font-semibold [overflow-wrap:anywhere]" style={{ color: 'var(--editor-text)', minHeight: 40 }}>
        {drill.title}
      </div>
      <div className="flex items-center justify-between gap-2" style={{ height: 36 }}>
        <span className={PHONE_CHIP} style={{ background: 'var(--editor-surface)', color: 'var(--editor-text-muted)' }}>
          {SHOT_TYPE_LABELS[drill.shotType]}
        </span>
        <div className="flex items-center gap-1 flex-none">
          <button type="button" onClick={() => onOpen(drill)} className={PHONE_GHOST} style={{ color: 'var(--editor-text-muted)' }} aria-label={`Info about ${drill.title}`}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
          </button>
          <button
            type="button"
            data-testid="drill-toggle-button"
            onClick={() => onToggle(drill)}
            className={PHONE_GHOST}
            style={selected ? { background: 'var(--color-biscayne)', color: '#fff' } : { color: 'var(--editor-text-muted)' }}
            aria-pressed={selected}
            aria-label={selected ? `Remove ${drill.title}` : `Add ${drill.title}`}
          >
            {selected ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
            )}
          </button>
        </div>
      </div>
    </div>
  );
});

/* Spec 15.26: what runs on the main thread from the tap to the next paint,
   for the debug overlay (editorDebug=1). */
function markTap(name: string) {
  performance.mark(`lve:${name}:tap`);
  requestAnimationFrame(() => requestAnimationFrame(() => {
    try {
      const m = performance.measure(`lve:${name}`, `lve:${name}:tap`);
      debugNote(`${name}: ${Math.round(m.duration)}ms to the next paint`);
    } catch { /* marks cleared */ }
  }));
}

/* ── Phone (spec 15.24): the drill's detail as an iOS sheet ─────────────────
   A bottom sheet (IosSheet, bare: grabber only): the thumbnail as a 16:9
   poster with a 44 play button (our player, a spinner until it can play),
   the title (Headline 17/22 semibold, two lines), the category chip, the
   description scrolling, and Close and Attach to Lesson pinned at the
   bottom, 44 capsules 12 apart. Dragging the grabber down dismisses it;
   landscape is the same sheet, 360 wide, centred. The sheet slides out
   (200ms) first and only then is the drill attached, so the draft write and
   the list update never share a frame with the close (15.26). */
function PhoneDrillSheet({ drill, selected, onClose, onToggle }: {
  drill: Drill;
  selected: boolean;
  onClose: () => void;
  onToggle: (drill: Drill) => void;
}) {
  const landscape = usePhoneLayout().landscape;
  const [dismissing, setDismissing] = useState(false);
  const afterRef = useRef<(() => void) | null>(null);
  const requestClose = useCallback((name: string, after?: () => void) => {
    markTap(name);
    afterRef.current = after ?? null;
    setDismissing(true);
  }, []);
  useEffect(() => {
    if (!dismissing) return;
    const t = setTimeout(() => {
      onClose();
      afterRef.current?.();
      afterRef.current = null;
    }, 200);
    return () => clearTimeout(t);
  }, [dismissing, onClose]);

  const hasVideo = !isPlaceholderUrl(drill.videoUrl);
  const [media, setMedia] = useState<'poster' | 'loading' | 'ready'>('poster');
  const footer = (
    <div className="flex gap-3">
      <button
        type="button"
        onClick={() => requestClose('drill-sheet-close')}
        className="flex-1 h-11 rounded-full text-[17px] leading-[22px] font-medium ios-press focus:outline-none focus-visible:ring-2 focus-visible:ring-db-accent/40"
        style={{ background: 'rgba(120, 120, 128, 0.16)', color: '#fff' }}
      >
        Close
      </button>
      <button
        type="button"
        data-testid="drill-sheet-attach"
        onClick={() => requestClose('drill-sheet-attach', () => onToggle(drill))}
        className="flex-1 h-11 rounded-full text-[17px] leading-[22px] font-medium ios-press focus:outline-none focus-visible:ring-2 focus-visible:ring-db-accent/40"
        style={{ background: 'var(--color-biscayne)', color: '#fff' }}
      >
        {selected ? 'Remove from Lesson' : 'Attach to Lesson'}
      </button>
    </div>
  );
  return (
    <>
      <div
        className="fixed inset-0"
        style={{ zIndex: 'var(--z-editor-menu)', background: 'rgba(0,0,0,0.4)', opacity: dismissing ? 0 : 1, transition: 'opacity 200ms ease' }}
        onClick={() => requestClose('drill-sheet-close')}
        aria-hidden="true"
      />
      <IosSheet bare dismissing={dismissing} landscape={landscape} title={drill.title} onDismiss={() => requestClose('drill-sheet-close')} testId="drill-sheet" footer={footer}>
        <div className="relative flex-none bg-black" style={{ aspectRatio: '16 / 9' }}>
          {media === 'poster' ? (
            <>
              <PhoneDrillThumb drill={drill} />
              {hasVideo && (
                <button
                  type="button"
                  onClick={() => setMedia('loading')}
                  aria-label="Play"
                  className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 rounded-full lve-glass flex items-center justify-center ios-press"
                >
                  <svg className="w-5 h-5 ml-0.5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z" /></svg>
                </button>
              )}
            </>
          ) : (
            <>
              <EditorVideo
                src={drill.videoUrl}
                autoPlay
                playsInline
                className="w-full h-full object-contain"
                wrapperClassName="w-full h-full"
                onCanPlay={() => setMedia('ready')}
              />
              {media === 'loading' && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none" aria-hidden="true">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-white/40 border-t-white" />
                </div>
              )}
            </>
          )}
        </div>
        <div className="flex-none px-4 pt-3">
          <h2 className="text-[17px] leading-[22px] font-semibold [overflow-wrap:anywhere]" style={{ color: 'var(--editor-text)' }}>{drill.title}</h2>
          <span className={`${PHONE_CHIP} mt-2`} style={{ background: 'var(--editor-surface)', color: 'var(--editor-text-muted)' }}>
            {SHOT_TYPE_LABELS[drill.shotType]}
          </span>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 pt-2 pb-2 text-[15px] leading-[20px] whitespace-pre-line" style={{ color: 'var(--editor-text)', minHeight: 44 }}>
          {drill.description}
        </div>
      </IosSheet>
    </>
  );
}

export function DrillBrowser({
  coachId,
  selectedDrillIds,
  onToggleDrill,
  inLessonEditor = false,
}: DrillBrowserProps) {
  /* In the editor a revisit starts from the session's list and does not fetch. */
  const cachedDrills = inLessonEditor ? editorDrillsCache.get(coachId) : undefined;
  const [drills, setDrills] = useState<Drill[]>(() => cachedDrills ?? []);
  const [loading, setLoading] = useState(!cachedDrills);
  const [error, setError] = useState(false);
  const [activeFilter, setActiveFilter] = useState<DrillShotType | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [infoModalDrill, setInfoModalDrill] = useState<Drill | null>(null);
  const [previewDrill, setPreviewDrill] = useState<Drill | null>(null);
  const [showAddDrill, setShowAddDrill] = useState(false);

  /* A new coach (or editor context) starts loading afresh. Set while
     rendering rather than in the effect below, which the React Compiler
     rule forbids; mount already starts in this state. */
  const fetchKey = `${coachId}|${inLessonEditor}`;
  const [fetchedKey, setFetchedKey] = useState(fetchKey);
  if (fetchedKey !== fetchKey) {
    setFetchedKey(fetchKey);
    setLoading(!(inLessonEditor && editorDrillsCache.has(coachId)));
    setError(false);
  }

  useEffect(() => {
    if (inLessonEditor && editorDrillsCache.has(coachId)) return;
    let cancelled = false;
    getAllDrillsForCoach(coachId)
      .then((results) => {
        if (inLessonEditor) editorDrillsCache.set(coachId, results);
        if (!cancelled) {
          setDrills(results);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDrills([]);
          setLoading(false);
          setError(true);
        }
      });
    return () => { cancelled = true; };
  }, [coachId, inLessonEditor]);

  const reloadDrills = () => {
    setLoading(true);
    setError(false);
    getAllDrillsForCoach(coachId)
      .then((results) => {
        // A retry or a newly added drill refreshes the editor's session list.
        if (inLessonEditor) editorDrillsCache.set(coachId, results);
        setDrills(results);
        setLoading(false);
      })
      .catch(() => {
        setDrills([]);
        setLoading(false);
        setError(true);
      });
  };

  const filtered = useMemo(() => {
    let list = drills;
    if (activeFilter !== 'all') {
      list = list.filter((d) => d.shotType === activeFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (d) =>
          d.title.toLowerCase().includes(q) ||
          d.description.toLowerCase().includes(q)
      );
    }
    return list;
  }, [drills, activeFilter, searchQuery]);

  const selectedSet = useMemo(() => new Set(selectedDrillIds), [selectedDrillIds]);
  /* Stable, so the memoised phone cards do not re-render on every change. */
  const openDrill = useCallback((d: Drill) => setPreviewDrill(d), []);
  const closeDrill = useCallback(() => setPreviewDrill(null), []);

  /* Phone: 20 rows at a time, 20 more as the end comes into view. A new
     filter or search starts again from the first 20. */
  const phoneList = usePhoneLayout().phone && inLessonEditor;
  const [shown, setShown] = useState(PHONE_DRILL_PAGE);
  const [listKey, setListKey] = useState(`${activeFilter}|${searchQuery}`);
  if (listKey !== `${activeFilter}|${searchQuery}`) {
    setListKey(`${activeFilter}|${searchQuery}`);
    setShown(PHONE_DRILL_PAGE);
  }
  const moreRef = useRef<HTMLDivElement>(null);
  const visible = phoneList ? filtered.slice(0, shown) : filtered;
  const hasMore = phoneList && visible.length < filtered.length;
  useEffect(() => {
    const el = moreRef.current;
    if (!hasMore || !el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) setShown((n) => n + PHONE_DRILL_PAGE);
    }, { rootMargin: '400px 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, shown]);

  /* Phone hit regions (GE-6), in the lesson editor only. The chips wrap onto
     a second line instead of scrolling sideways; at 32 tall with 12 between
     lines, a transparent ::after takes each to 44 without reaching the next
     line. The card's two round buttons grow to 32 and sit 44 apart, so their
     targets meet without overlapping. */
  const phoneSearch = inLessonEditor ? 'phone:h-11' : '';
  const phoneChipRow = inLessonEditor ? 'phone:flex-wrap phone:overflow-visible phone:gap-y-3 phone:pt-1.5 phone:pb-1.5' : '';
  const phoneChip = inLessonEditor
    ? "phone:min-h-8 phone:min-w-[44px] phone:relative phone:after:absolute phone:after:inset-x-0 phone:after:-inset-y-1.5 phone:after:content-['']"
    : '';
  const phoneCardActions = inLessonEditor ? 'phone:gap-3' : '';
  const phoneRoundBtn = inLessonEditor
    ? "phone:w-8 phone:h-8 phone:relative phone:after:absolute phone:after:-inset-1.5 phone:after:content-['']"
    : '';

  return (
    <div className="flex flex-col gap-3">
      {/* 1. Search bar */}
      <div className="relative">
        <svg
          className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#5C6B75]"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search drills…"
          className={`w-full bg-[#1A2B42] border border-[#2A3F5C] rounded-lg pl-8 pr-3 py-1.5 text-[16px] leading-[21px] text-[#F1F5F9] placeholder-[#5C6B75] focus:outline-none focus:border-[#1A6B8A] ${phoneSearch}`}
        />
      </div>

      {/* 2. Shot type filter chips */}
      <div className={`flex gap-1.5 overflow-x-auto pb-1 ${phoneChipRow}`} style={{ scrollbarWidth: 'thin' }}>
        <button
          type="button"
          onClick={() => setActiveFilter('all')}
          className={`shrink-0 px-3 py-1 rounded-full text-xs font-medium transition-colors ${phoneChip} ${
            activeFilter === 'all'
              ? 'bg-[#1A6B8A] text-white'
              : 'bg-[#1A2B42] text-[#8899AA] hover:text-[#F1F5F9]'
          }`}
        >
          All
        </button>
        {SHOT_TYPE_ORDER.map((shotType) => (
          <button
            key={shotType}
            type="button"
            onClick={() => setActiveFilter(shotType)}
            className={`shrink-0 px-3 py-1 rounded-full text-xs font-medium transition-colors ${phoneChip} ${
              activeFilter === shotType
                ? 'bg-[#1A6B8A] text-white'
                : 'bg-[#1A2B42] text-[#8899AA] hover:text-[#F1F5F9]'
            }`}
          >
            {SHOT_TYPE_LABELS[shotType]}
          </button>
        ))}
      </div>

      {/* 3. Drill grid */}
      {loading ? (
        <div className={phoneList ? 'grid grid-cols-2 gap-3' : 'grid grid-cols-2 gap-2'}>
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex flex-col gap-1.5">
              <div className={`aspect-video bg-[#1A2B42] animate-pulse ${phoneList ? 'rounded-[10px]' : 'rounded-lg'}`} />
              <div className="h-3 bg-[#1A2B42] animate-pulse rounded w-3/4" />
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-10 text-center">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#8899AA" strokeWidth="2" className="mb-2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <div className="text-sm font-medium text-[#F1F5F9] mb-1">Couldn't load drills</div>
          <div className="text-xs text-[#5C6B75] mb-3">Check your connection and try again</div>
          <button
            type="button"
            onClick={reloadDrills}
            className="px-4 py-1.5 bg-[#1A6B8A] text-white text-xs font-medium rounded-lg hover:bg-[#0F4A63] transition-colors"
          >
            Retry
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 text-center">
          <div className="text-sm font-medium text-[#F1F5F9] mb-1">No drills found</div>
          <div className="text-xs text-[#5C6B75]">
            {searchQuery.trim() ? 'Try a different search' : 'No drills for this shot type'}
          </div>
        </div>
      ) : (
        <div className={phoneList ? 'grid grid-cols-2 gap-3' : 'grid grid-cols-2 gap-2'}>
          {visible.map((drill) => {
            const isSelected = selectedSet.has(drill.id);
            if (phoneList) {
              return <PhoneDrillCard key={drill.id} drill={drill} selected={isSelected} onOpen={openDrill} onToggle={onToggleDrill} />;
            }
            return (
              <div
                key={drill.id}
                data-testid="drill-browser-item"
                className={`flex flex-col gap-1.5 rounded-lg p-1 btn-press ${
                  isSelected ? 'ring-2 ring-[#1A6B8A]' : ''
                }`}
              >
                <button
                  type="button"
                  onClick={() => setPreviewDrill(drill)}
                  className="aspect-video bg-[#0F1A2E] rounded-lg overflow-hidden relative flex items-center justify-center group"
                >
                  {phoneList ? (
                    <PhoneDrillThumb drill={drill} />
                  ) : inLessonEditor ? (
                    <EditorDrillFrame drill={drill} />
                  ) : drill.thumbnailUrl ? (
                    <img
                      src={drill.thumbnailUrl}
                      alt={drill.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <VideoFrameThumb
                      videoUrl={isPlaceholderUrl(drill.videoUrl) ? null : drill.videoUrl}
                      alt={drill.title}
                      className="w-full h-full object-cover"
                      placeholder={
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="#5C6B75">
                          <polygon points="6 4 20 12 6 20 6 4" />
                        </svg>
                      }
                    />
                  )}
                  {/* Hover play overlay */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                    <div className="w-10 h-10 rounded-full bg-white/0 group-hover:bg-white/90 transition-all flex items-center justify-center">
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="#0F1A2E"
                        className="opacity-0 group-hover:opacity-100 transition-opacity ml-0.5"
                      >
                        <polygon points="6 4 20 12 6 20 6 4" />
                      </svg>
                    </div>
                  </div>
                </button>
                <div className={`flex items-center gap-1 px-0.5 ${phoneCardActions}`}>
                  <div className="min-w-0 text-xs font-medium text-[#F1F5F9] [overflow-wrap:anywhere] flex-1">
                    {drill.title}
                  </div>
                  <button
                    type="button"
                    onClick={() => setInfoModalDrill(drill)}
                    className={`w-6 h-6 rounded-full flex items-center justify-center text-[#8899AA] hover:text-[#F1F5F9] hover:bg-[#1A2B42] transition-colors shrink-0 ${phoneRoundBtn}`}
                    aria-label={`Info about ${drill.title}`}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="16" x2="12" y2="12" />
                      <line x1="12" y1="8" x2="12.01" y2="8" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    data-testid="drill-toggle-button"
                    onClick={() => onToggleDrill(drill)}
                    className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 transition-colors ${phoneRoundBtn} ${
                      isSelected
                        ? 'bg-[#1A6B8A] text-white'
                        : 'bg-[#1A2B42] text-[#8899AA] hover:text-[#F1F5F9]'
                    }`}
                    aria-label={isSelected ? `Remove ${drill.title}` : `Add ${drill.title}`}
                  >
                    {isSelected ? (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    ) : (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            );
          })}
          {/* Phone: the next 20 load as this nears the screen. */}
          {hasMore && <div ref={moreRef} className="col-span-2 h-px" aria-hidden="true" />}
        </div>
      )}

      {/* 4. Upload Custom Drill */}
      <button
        type="button"
        onClick={() => setShowAddDrill(true)}
        className={`w-full flex items-center justify-center gap-1.5 bg-[#1A2B42] text-[#8899AA] hover:text-[#F1F5F9] border border-dashed border-[#2A3F5C] rounded-lg py-2 text-xs transition-colors ${inLessonEditor ? 'phone:min-h-[44px]' : ''}`}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="17 8 12 3 7 8" />
          <line x1="12" y1="3" x2="12" y2="15" />
        </svg>
        Upload Custom Drill
      </button>

      {/* Add drill modal */}
      <AddDrillModal
        isOpen={showAddDrill}
        onClose={() => setShowAddDrill(false)}
        coachId={coachId}
        onDrillCreated={(drillId) => {
          setShowAddDrill(false);
          getAllDrillsForCoach(coachId).then((updated) => {
            if (inLessonEditor) editorDrillsCache.set(coachId, updated);
            setDrills(updated);
            const newDrill = updated.find((d) => d.id === drillId);
            if (newDrill) onToggleDrill(newDrill);
          });
        }}
      />

      {/* 5. Info modal */}
      <AnimatePresence>
        {infoModalDrill && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setInfoModalDrill(null)}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#152238] border border-[#2A3F5C] rounded-xl p-5 max-w-md w-full shadow-2xl"
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <h2 className="text-base font-bold text-[#F1F5F9]">{infoModalDrill.title}</h2>
                <button
                  type="button"
                  onClick={() => setInfoModalDrill(null)}
                  className="w-7 h-7 rounded-full flex items-center justify-center text-[#8899AA] hover:text-[#F1F5F9] hover:bg-[#1A2B42] shrink-0"
                  aria-label="Close"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
              <div className="inline-block px-2 py-0.5 rounded-full bg-[#1A2B42] text-[#8899AA] text-[10px] font-medium mb-3">
                {SHOT_TYPE_LABELS[infoModalDrill.shotType]}
              </div>
              <p className="text-sm text-[#D1D9E0] leading-relaxed whitespace-pre-line mb-5">
                {infoModalDrill.description}
              </p>
              <button
                type="button"
                onClick={() => setInfoModalDrill(null)}
                className="w-full bg-[#1A6B8A] text-white text-sm font-semibold rounded-lg py-2 btn-press"
              >
                Got it
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 6. Video preview: a sheet on a phone (spec 15.24), the modal elsewhere. */}
      {phoneList && previewDrill && (
        <PhoneDrillSheet key={previewDrill.id} drill={previewDrill} selected={selectedSet.has(previewDrill.id)} onClose={closeDrill} onToggle={onToggleDrill} />
      )}
      <AnimatePresence>
        {!phoneList && previewDrill && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setPreviewDrill(null)}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              onClick={(e) => e.stopPropagation()}
              className={`bg-[#152238] border border-[#2A3F5C] rounded-xl overflow-hidden max-w-lg w-full shadow-2xl${phoneList ? ' flex flex-col' : ''}`}
              /* Phone (spec 13.3): the card never outgrows the screen, so an
                 expanded description scrolls and the buttons stay at the bottom. */
              style={phoneList ? { maxHeight: 'calc(var(--vv-height, 100dvh) - 32px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))' } : undefined}
            >
              {/* Video area */}
              <div className={`aspect-video bg-black relative${phoneList ? ' flex-none' : ''}`}>
                {previewDrill.videoUrl && !previewDrill.videoUrl.startsWith('https://placeholder') ? (
                  inLessonEditor ? (
                    <EditorVideo
                      src={previewDrill.videoUrl}
                      controls
                      autoPlay
                      playsInline
                      className="w-full h-full object-contain"
                      wrapperClassName="w-full h-full"
                    />
                  ) : (
                  <video
                    src={previewDrill.videoUrl}
                    controls
                    autoPlay
                    playsInline
                    className="w-full h-full object-contain"
                  />
                  )
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-[#5C6B75]">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                      <polygon points="6 4 20 12 6 20 6 4" />
                    </svg>
                    <span className="text-xs mt-2">Video preview not available</span>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className={phoneList ? 'p-4 flex flex-col min-h-0' : 'p-4'}>
                <div className={phoneList ? 'min-h-0 overflow-y-auto' : undefined}>
                <div className="flex items-start justify-between gap-3 mb-1">
                  <h3 className="text-sm font-bold text-[#F1F5F9]">{previewDrill.title}</h3>
                  <span className="shrink-0 px-2 py-0.5 rounded-full bg-[#1A2B42] text-[#8899AA] text-[10px] font-medium">
                    {SHOT_TYPE_LABELS[previewDrill.shotType]}
                  </span>
                </div>
                {previewDrill.description && (phoneList ? (
                  <ExpandableDescription key={previewDrill.id} text={previewDrill.description} />
                ) : (
                  <p className="text-xs text-[#8899AA] leading-relaxed [overflow-wrap:anywhere] mb-3">
                    {previewDrill.description}
                  </p>
                ))}
                </div>

                <div className={phoneList ? 'flex gap-2 flex-none' : 'flex gap-2'}>
                  <button
                    type="button"
                    onClick={() => setPreviewDrill(null)}
                    className="flex-1 bg-[#1A2B42] text-[#D1D9E0] text-sm font-medium rounded-lg py-2 hover:bg-[#253756] transition-colors"
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      onToggleDrill(previewDrill);
                      setPreviewDrill(null);
                    }}
                    className={`flex-1 text-sm font-semibold rounded-lg py-2 btn-press transition-colors ${
                      selectedSet.has(previewDrill.id)
                        ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                        : 'bg-[#1A6B8A] text-white hover:bg-[#0F4A63]'
                    }`}
                  >
                    {selectedSet.has(previewDrill.id) ? 'Remove from Lesson' : 'Attach to Lesson'}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
